import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Layers } from 'lucide-react';

interface TemplateLinkedCardProps {
  templateId: string;
  templateName: string;
  onUnlink: () => void;
}

/**
 * Displays a linked transaction template badge with an inline Unlink confirmation.
 * Used in SubscriptionDetailSheet to show and manage the template association.
 */
export function TemplateLinkedCard({ templateName, onUnlink }: TemplateLinkedCardProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="template-linked-card">
      <Badge variant="secondary" className="flex items-center gap-1 text-xs">
        <Layers className="h-3 w-3" />
        Linked Template: {templateName}
      </Badge>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-0 px-1 text-xs text-muted-foreground hover:text-destructive"
            data-testid="template-unlink-btn"
          >
            Unlink
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56" align="start">
          <p className="text-sm font-medium mb-3">Unlink this template?</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="flex-1"
              data-testid="template-unlink-confirm-btn"
              onClick={() => {
                onUnlink();
                setOpen(false);
              }}
            >
              Confirm
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
